import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listDocuments,
  uploadDocument,
  updateDocument,
  archiveDocument,
  getDocumentDownloadUrl,
  listDocumentSends,
  createDocumentSend,
  type DocumentDocType,
} from "@/api/documents";

const DOCUMENTS_KEY = ["documents"] as const;
const SENDS_KEY = ["document-sends"] as const;

export function useDocuments(params?: {
  docType?: string;
  jobId?: string;
  companyId?: string;
  includeArchived?: boolean;
}) {
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, params ?? {}],
    queryFn: async () => (await listDocuments(params)).documents,
    staleTime: 30_000,
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      docId,
      patch,
    }: {
      docId: string;
      patch: Parameters<typeof updateDocument>[1];
    }) => updateDocument(docId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });
}

export function useArchiveDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) => archiveDocument(docId),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEY }),
  });
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (docId: string) => {
      const { url, fileName } = await getDocumentDownloadUrl(docId);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
      return { url, fileName };
    },
  });
}

export function useDocumentSends(jobId?: string) {
  return useQuery({
    queryKey: [...SENDS_KEY, jobId ?? "all"],
    queryFn: async () =>
      (await listDocumentSends(jobId ? { jobId } : undefined)).sends,
    enabled: jobId !== undefined ? Boolean(jobId) : true,
    staleTime: 30_000,
  });
}

export function useCreateDocumentSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDocumentSend,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SENDS_KEY });
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEY });
    },
  });
}

export type { DocumentDocType };
